import { format, isSameDay } from 'date-fns';
import { useState } from 'react';

import { AverageConsumption } from '../../../../../types/AverageConsumption';
import { ConsumptionPreset } from '../../../../../types/ConsumptionPresetsRequestMessage';
import { Text } from '../../../../cdl/Text/Text';
import { Tooltip } from '../../../../cdl/Tooltip/Tooltip';
import { formatDateTime } from '../../../../common/helpers/formatDateTime.helper';
import { formatNumber } from '../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { Box } from '../../../../common/ui/Box';

import { AverageConsumptionLineChart } from './AverageConsumptionLineChart';
import { Difference } from './DIfference';
import { QualityIndicator } from './QualityIndicator';

interface AverageConsumptionCategoryProps {
    consumptions: AverageConsumption[];
    preset: ConsumptionPreset;
    dateRange: Date[];
}

export const AverageConsumptionCategory = ({ consumptions, preset, dateRange }: AverageConsumptionCategoryProps) => {
    const { getHumanReadableValue } = useEnums();
    const lastConsumptionOfRange = consumptions.at(-1);
    const [activeConsumption, setActiveConsumption] = useState<AverageConsumption | null>(
        lastConsumptionOfRange || null
    );

    const isToday = isSameDay(dateRange[1], new Date());
    const endDate = isToday ? translate('vessel.averageConsumption.today') : format(dateRange[1], 'MMM d');

    const handleActiveElementChange = (averageConsumption?: AverageConsumption) => {
        if (averageConsumption) {
            setActiveConsumption(averageConsumption);
        } else {
            setActiveConsumption(lastConsumptionOfRange || null);
        }
    };

    const averageConsumption = preset.excludePortCalls
        ? activeConsumption?.averageConsumptionExclDaysAtPort
        : activeConsumption?.averageConsumption;

    return (
        <Box display="grid" gap={8} gridTemplateColumns="minmax(0, 1fr)">
            <Box display="flex" justifyContent="space-between">
                <Box display="flex" gap={8} alignItems="center">
                    <Text fontWeight={600} variant="body">
                        {getHumanReadableValue('vesselTankCategory', preset.vesselTankCategory)}
                    </Text>
                    {activeConsumption ? (
                        <QualityIndicator qualityCategory={activeConsumption.qualityCategory} />
                    ) : null}
                </Box>
                {preset.fixedAverageDailyConsumption && averageConsumption ? (
                    <Tooltip
                        maxWidth="200px"
                        showArrow
                        label={translate('vessel.averageConsumption.differenceTooltip')}
                    >
                        <div>
                            <Difference
                                fixedAverageConsumption={preset.fixedAverageDailyConsumption}
                                dynamicAverageConsumption={averageConsumption}
                            />
                        </div>
                    </Tooltip>
                ) : null}
            </Box>

            <Box display="flex" justifyContent="space-between">
                <div>
                    {preset.fixedAverageDailyConsumption ? (
                        <Text variant="body" color="foreground.muted">
                            {preset.fixedAverageDailyConsumption}L
                        </Text>
                    ) : null}
                </div>

                <div>
                    {typeof averageConsumption === 'number' ? (
                        <Text variant="body" color="accent.foreground" fontWeight={600}>
                            {formatNumber({
                                number: averageConsumption,
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                            L
                        </Text>
                    ) : null}
                </div>
            </Box>

            <AverageConsumptionLineChart
                consumptions={consumptions}
                preset={preset}
                dateRange={dateRange}
                onActiveElementChange={handleActiveElementChange}
            />

            <Box display="flex" justifyContent="space-between">
                <Text variant="extraSmall" color="foreground.subtle">
                    {formatDateTime({ date: dateRange[0].toISOString(), format: 'MMM d' })}
                </Text>
                <Text variant="extraSmall" color="foreground.subtle">
                    {endDate}
                </Text>
            </Box>
        </Box>
    );
};
